import FullCalendar from '@fullcalendar/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import TranslateUtils from '../../../i18n/TranslateUtils'
import PageContentWrapper from '../../layout/PageContentWrapper'

import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Calendar} from 'primereact/calendar'
import {Checkbox} from 'primereact/checkbox'
import {ProgressSpinner} from 'primereact/progressspinner'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {getLanguageCode} from '../../../i18n/LanguageUtils'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {
    obsEvent,
    removeOnMenuChanged,
} from '../../observations/ObservationService'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import CalendarForm from '../../reminder/CalendarForm'
import {
    CalendarFormType,
    reminderStatus,
    reminderTypes,
} from '../../reminder/ReminderConstants'
import {
    useListReminders,
    useReminderUpdateStatus,
} from '../../reminder/query-keys/ReminderKeysManager'
import {convertKeyToLabel} from '../../table-utils/TableUtils'
import ReservationDialog from './ReservationDialog'
import {
    calendarItemEvent,
    calendarItemEventTypes,
    getCalendarItemEventKey,
} from './broadcast/CalendarItemEvent'
import './style.scss'

// TODO: Translate
const StyledReservationDataTable = () => {
    useSignals()
    const {translateCommons, translateKeys, translateDatatable} =
        TranslateUtils()

    const {user} = useAuth()
    const {userRole, timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const calendarRef = useRef(null)
    const dialogRef = useRef(null)
    const calendarForm = useRef()

    //#region Query
    const queryClient = useQueryClient()
    const currentView = useSignal('timeGridWeek')

    const getDateOfWeek = dateValue => {
        const currentDate = dateValue

        const currentDay = currentDate.getDay()

        const startDate = new Date(currentDate)
        startDate.setDate(currentDate.getDate() - currentDay)

        let dates = []
        for (let i = 0; i < 7; i++) {
            const nextDate = new Date(startDate)
            nextDate.setDate(startDate.getDate() + i)
            dates.push(moment(nextDate).format('yyyy-MM-DD'))
        }

        return dates
    }

    const title = useSignal('')
    const filterRequest = useSignal({
        statusList: [
            reminderStatus.done,
            reminderStatus.missed,
            reminderStatus.reschedule,
            reminderStatus.brandNew,
        ],
        dateIn: getDateOfWeek(new Date()),
    })

    const {data, isFetching} = useListReminders(user?.id, timezoneId, {
        type: 'appointment',
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        ownerId: user?.id,
        ...filterRequest.value,
    })

    // Date Format for filter calendar
    const dataFormated = useMemo(() => {
        if (data && data?.length) {
            let holderList = []
            for (let calendarItem of data) {
                let clientTZ = moment.tz(
                    moment(calendarItem?.start).format('YYYY-MM-DD HH:mm'),
                    'YYYY-MM-DD HH:mm',
                    timezoneId,
                )
                let UTC = moment.utc(clientTZ)
                let currentTZ = moment.tz(
                    UTC,
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                )

                holderList.push({
                    ...calendarItem,
                    start: currentTZ.format('YYYY-MM-DD HH:mm'),
                    end: currentTZ.format('YYYY-MM-DD HH:mm'),
                    dateTime: currentTZ.format('YYYY-MM-DD HH:mm'),
                })
            }

            return holderList
        }

        return []
    }, [data, timezoneId])

    const getTitleFormated = date => {
        let dateList
        if (!date) {
            dateList = getDateOfWeek(new Date())
        } else {
            dateList = getDateOfWeek(date)
        }

        if (dateList?.length) {
            let dateStart = moment(dateList[0]).format('MMM Do, YYYY')
            let dateEnd = moment(dateList[dateList?.length - 1]).format(
                'MMM Do, YYYY',
            )

            return `${dateStart} - ${dateEnd}`
        }
    }
    //#endregion

    //#region Use Effect
    useEffect(() => {
        obsEvent.on('MENU-CHANGED', () => {
            setTimeout(() => {
                calendarRef.current?.getApi()?.updateSize()
            }, 200)

            clearTimeout()
        })

        return () => {
            removeOnMenuChanged()
        }
    }, [data])

    //#region Event Listener on State Changes
    useEffect(() => {
        // CALENDAR ITEM CHANGE
        calendarItemEvent.on(
            getCalendarItemEventKey(calendarItemEventTypes.TABLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (!data?.ownerId || user?.id !== data?.ownerId) {
                    return
                }

                const {reminderKeys} = QueryKeyManager(
                    QueryKeyTypes.reminder,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: reminderKeys.pattern(),
                })
            },
        )
    }, [])
    //#endregion

    //#region Render Event
    const mapColorAppointmentStatus = status => {
        if (status === reminderStatus.done) {
            return {
                backgroundColor: 'rgba(228, 248, 240, 1)',
                textColor: '#1ea97c',
                borderColor: '#1ea97c',
            }
        } else if (status === reminderStatus.missed) {
            return {
                textColor: '#ff5757',
                backgroundColor: 'rgba(255, 231, 230, 1)',
                borderColor: '#ff5757',
            }
        } else if (status === reminderStatus.brandNew) {
            return {
                backgroundColor: 'rgba(219, 234, 254, 1)',
                textColor: '#3b82f6',
                borderColor: '#3b82f6',
            }
        } else if (status === reminderStatus.reschedule) {
            return {
                backgroundColor: 'rgba(255, 242, 226, 1)',
                textColor: '#cc8925',
                borderColor: '#cc8925',
            }
        }
    }

    const getIconByAppointmentStatus = status => {
        if (status === reminderStatus.done) {
            return <i className="pi pi-check"></i>
        } else if (status === reminderStatus.missed) {
            return <i className="pi pi-times"></i>
        } else if (status === reminderStatus.brandNew) {
            return <i className="pi pi-clock"></i>
        } else if (status === reminderStatus.reschedule) {
            return <i className="pi pi-replay"></i>
        } else {
            return <i className="pi pi-clock"></i>
        }
    }

    const {mutateAsync: updateStatus} = useReminderUpdateStatus(user?.id)

    const handleUpdateStatus = async (data, status) => {
        let toPostData = {...data, status}
        const responseData = await updateStatus(toPostData)
        if (responseData?.id) {
            const {reminderKeys} = QueryKeyManager(
                QueryKeyTypes.reminder,
                user?.id,
            )

            queryClient.invalidateQueries(reminderKeys.pattern())
        }
    }

    const renderListEventCalendar = useCallback(
        (event, eventProps) => {
            let currentTZ = moment(
                moment(event.event?.start).format('DD-MM-YYYY HH:mm A'),
                'DD-MM-YYYY HH:mm A',
            )

            let UTC = moment.utc(currentTZ)
            let clientTZ = moment.tz(UTC, timezoneId)

            let appointmentData = {
                id: event.event.id ? parseInt(event.event.id) : null,
                dateAndTime: event.event?.start,
                ...eventProps,
            }

            let dateMax = new Date(event.event?.start)
            dateMax.setHours(dateMax.getHours() + 2)
            const isMissed = dateMax.getTime() < Date.now()

            return (
                <>
                    <div
                        className="border-none h-full text-color p-1 w-full"
                        onClick={() => {
                            dialogRef.current.showForm(appointmentData)
                        }}>
                        {eventProps?.status ? (
                            <>
                                <div
                                    className="p-1 border-round text-white flex align-items-center mb-2 w-full "
                                    style={{
                                        backgroundColor:
                                            mapColorAppointmentStatus(
                                                eventProps?.status ===
                                                    reminderStatus.done
                                                    ? reminderStatus.done
                                                    : isMissed
                                                    ? reminderStatus.missed
                                                    : eventProps?.status,
                                            ).textColor,
                                    }}>
                                    {getIconByAppointmentStatus(
                                        eventProps?.status ===
                                            reminderStatus.done
                                            ? reminderStatus.done
                                            : isMissed
                                            ? reminderStatus.missed
                                            : eventProps?.status,
                                    )}
                                    <p className="ml-2 overflow-dot overflow-text-1">
                                        {translateCommons(
                                            convertKeyToLabel(
                                                eventProps?.status ===
                                                    reminderStatus.done
                                                    ? reminderStatus.done
                                                    : isMissed
                                                    ? reminderStatus.missed
                                                    : eventProps?.status,
                                            ),
                                        )}
                                    </p>
                                </div>
                                <div className="grid col-12 align-items-stretch">
                                    <div className="col-12 md:col-5 lg:col-5">
                                        <div className="w-full">
                                            <p className="text-500 text-md mb-0">
                                                {translateCommons(
                                                    'Patient Name',
                                                )}
                                            </p>
                                            <p className="font-semibold m-0">
                                                {eventProps?.userInfo?.fullName}
                                            </p>
                                        </div>
                                        <div className="w-full mb-2">
                                            <p className="text-500 text-md mb-0">
                                                {translateCommons(
                                                    'Appointment Type',
                                                )}
                                            </p>
                                            <p className="font-semibold m-0">
                                                {eventProps?.appointmentType}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-7 lg:col-7 surface-50 border-round">
                                        <h5
                                            className={
                                                'font-bold col-12 p-0 text-sm sm:text-sm md:text-xl lg:text-xl m-0 mb-2'
                                            }>
                                            <i className="pi pi-info-circle mr-2"></i>
                                            {translateCommons(`NOTE`)}
                                        </h5>
                                        <div className="flex flex-column gap-3 px-1">
                                            <p>
                                                {translateCommons(
                                                    "The actual date time of this appointment in the client's time zone is",
                                                )}{' '}
                                                <span className="font-bold text-blue-500">
                                                    {`${clientTZ.format(
                                                        'MMMM Do YYYY, HH:mm A',
                                                    )} (GMT ${clientTZ.format(
                                                        'Z',
                                                    )})`}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="uppercase font-normal text-sm mb-0">
                                {event.timeText}
                            </p>
                        )}
                    </div>
                    <div className="flex align-items-center justify-content-end z-6">
                        <Button
                            className="text-sm px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-500 border-blue-300"
                            icon="pi pi-calendar-minus"
                            label={translateCommons('Reschedule')}
                            onClick={() => {
                                calendarForm.current.showForm(appointmentData)
                            }}
                        />

                        {appointmentData?.appointmentType !== 'missed' && (
                            <Button
                                className="text-sm ml-2 px-5 py-2 z-6 p-button-outlined"
                                icon="pi pi-check-circle"
                                label={translateCommons('Done')}
                                onClick={() =>
                                    handleUpdateStatus(
                                        appointmentData,
                                        reminderStatus.done,
                                    )
                                }
                            />
                        )}
                    </div>
                    <hr />
                </>
            )
        },
        [timezoneId, translateCommons],
    )

    const renderdayGridEventCalendar = useCallback(
        (event, eventProps) => {
            let currentTZ = moment(
                moment(event.event?.start).format('DD-MM-YYYY HH:mm A'),
                'DD-MM-YYYY HH:mm A',
            )

            let UTC = moment.utc(currentTZ)
            let clientTZ = moment.tz(UTC, timezoneId)

            let appointmentData = {
                id: event.event.id ? parseInt(event.event.id) : null,
                dateAndTime: event.event?.start,
                ...eventProps,
            }

            let dateMax = new Date(event.event?.start)
            dateMax.setHours(dateMax.getHours() + 2)
            const isMissed = dateMax.getTime() < Date.now()

            return (
                <div
                    className="border-none h-full p-1 w-full cursor-pointer select-auto z-10"
                    onClick={() => {
                        dialogRef.current.showForm(appointmentData)
                    }}>
                    {eventProps?.status ? (
                        <>
                            <div className="w-full">
                                <p
                                    className="font-semibold m-0 uppercase"
                                    style={{
                                        color: mapColorAppointmentStatus(
                                            eventProps?.status ===
                                                reminderStatus.done
                                                ? reminderStatus.done
                                                : isMissed
                                                ? reminderStatus.missed
                                                : eventProps?.status,
                                        ).textColor,
                                    }}>
                                    {event.timeText}
                                </p>
                            </div>
                            <div className="w-full text-color">
                                <p className="text-500 text-xs mb-0 font-semibold">
                                    {translateCommons('Patient Name')}
                                </p>
                                <p className="font-semibold m-0">
                                    {eventProps?.userInfo?.fullName}
                                </p>
                            </div>
                            <div className="w-full text-color">
                                <p className="text-500 text-xs mb-0 font-semibold">
                                    {translateCommons('Appointment Type')}
                                </p>
                                <p className="font-semibold m-0">
                                    {eventProps?.appointmentType}
                                </p>
                            </div>
                        </>
                    ) : (
                        <p className="uppercase font-normal text-sm mb-0">
                            {event.timeText}
                        </p>
                    )}
                </div>
            )
        },
        [timezoneId, translateCommons],
    )
    //#endregion

    //#region Handle
    const handleOnSelectStatus = status => {
        if (filterRequest.value && filterRequest.value?.statusList) {
            let statusList = filterRequest.value?.statusList

            if (statusList?.length && statusList?.includes(status)) {
                statusList = statusList.filter(item => item !== status)
            } else {
                statusList.push(status)
            }

            filterRequest.value = {
                ...filterRequest.value,
                statusList: [...statusList],
            }
        } else {
            filterRequest.value = {
                statusList: [status],
            }
        }
    }

    const handleClearFilter = () => {
        filterRequest.value = {
            statusList: [
                reminderStatus.done,
                reminderStatus.missed,
                reminderStatus.reschedule,
                reminderStatus.brandNew,
            ],
            dateFrom: '',
            dateTo: '',
            dateIn: getDateOfWeek(new Date()),
        }

        // Set Default Title
        title.value = getTitleFormated(new Date())
    }
    //#endregion

    const renderEventContent = useCallback(
        event => {
            const eventProps = event.event?.extendedProps
            if (event.view.type === 'listWeek') {
                event.borderColor = 'transparent'
                event.backgroundColor = '#D8D9DA'
                return renderListEventCalendar(event, eventProps)
            } else if (event.view.type === 'timeGridWeek') {
                let dateMax = new Date(event.event?.start)
                dateMax.setHours(dateMax.getHours() + 2)
                const isMissed = dateMax.getTime() < Date.now()

                const colorObj = mapColorAppointmentStatus(
                    eventProps?.status === reminderStatus.done
                        ? reminderStatus.done
                        : isMissed
                        ? reminderStatus.missed
                        : eventProps?.status,
                )
                event.borderColor = colorObj?.borderColor

                event.backgroundColor = colorObj?.backgroundColor
                // event.textColor = colorObj.textColor

                return renderdayGridEventCalendar(event, eventProps)
            }
        },
        [renderListEventCalendar, renderdayGridEventCalendar],
    )

    const renderTodayButton = () => {
        let isToday = true
        if (calendarRef?.current) {
            const dateSelected = calendarRef?.current
                ?.getApi()
                .getCurrentData()?.currentDate

            isToday =
                dateSelected?.getDate() === new Date()?.getDate() &&
                dateSelected.getMonth() === new Date().getMonth() &&
                dateSelected.getFullYear() === new Date().getFullYear()
        }

        return (
            <div
                className={`${!isToday ? '' : 'opacity-50 select-none'}`}
                style={{
                    cursor: !isToday ? 'pointer' : 'not-allowed',
                }}>
                Today
            </div>
        )
    }

    return (
        <PageContentWrapper>
            <div className="grid align-items-start">
                <div className="lg:col-3 col-12">
                    <div className="col-12">
                        <div className="flex flex-wrap align-items-end w-full mb-3">
                            <div className="w-full flex justify-content-between align-items-center">
                                <h4 className="m-0 font-bold mr-3">
                                    {translateCommons('Create New')}
                                </h4>
                            </div>
                            <p className="text-700">
                                {translateCommons(
                                    'Create a new appointment for the designated patient and send them a notification when the appointment time begins.',
                                )}
                            </p>
                            <Button
                                className="w-full"
                                label={translateCommons('New schedule')}
                                icon="pi pi-plus-circle text-xl"
                                onClick={() =>
                                    calendarForm?.current?.showForm({
                                        type: reminderTypes.appointment,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div id="calendar-custom" className="col-12">
                        <div className="flex flex-wrap align-items-end w-full mb-3">
                            <div className="w-full flex justify-content-between align-items-center">
                                <h4 className="m-0 font-bold mr-3">
                                    {translateCommons('Filter by date')}
                                </h4>
                                <p
                                    className="text-primary cursor-pointer"
                                    onClick={handleClearFilter}>
                                    <i className="pi pi-filter-slash mr-2"></i>
                                    {translateDatatable('Clear')}
                                </p>
                            </div>
                            <p className="text-700">
                                {translateCommons(
                                    'Please adjust the filter to view data on meetings within a specific time frame.',
                                )}
                            </p>
                        </div>
                        <Calendar
                            locale={
                                getLanguageCode() === 'en' ||
                                getLanguageCode() === 'gb'
                                    ? 'en'
                                    : getLanguageCode()
                            }
                            className="border-300"
                            panelStyle={{
                                borderColor: 'var(--surface-300)',
                            }}
                            inline
                            value={filterRequest.value?.date}
                            onChange={e => {
                                filterRequest.value = {
                                    ...filterRequest.value,
                                    dateFrom: moment(e.value).format(
                                        'yyyy-MM-DD',
                                    ),
                                    dateTo: moment(e.value).format(
                                        'yyyy-MM-DD',
                                    ),
                                    dateIn: null,
                                    date: moment(e.value).format('yyyy-MM-DD'),
                                }

                                calendarRef?.current
                                    ?.getApi()
                                    ?.gotoDate(e.value)

                                title.value = moment(e.value).format(
                                    'MMMM Do YYYY',
                                )
                            }}
                        />
                    </div>
                    <div className="col-12">
                        <div className="flex flex-wrap align-items-end w-full mb-3">
                            <h4 className="m-0 font-bold mr-3">
                                {translateCommons('Event Statuses')}
                            </h4>
                            <p className="text-700">
                                {translateCommons(
                                    'Select the event statuses to display on the calendar.',
                                )}
                            </p>
                        </div>
                        <div className="p-2 w-full border-1 border-300 border-round">
                            {Object.keys(reminderStatus)?.map(key => {
                                const colorObj = mapColorAppointmentStatus(key)

                                return (
                                    <div
                                        className={`flex gap-2 align-items-center m-2 p-2 border-round hover:surface-200 cursor-pointer`}
                                        key={key}
                                        onClick={() =>
                                            handleOnSelectStatus(key)
                                        }>
                                        <Checkbox
                                            className="custom-reservations-cb"
                                            checked={
                                                isArrayAndNotEmpty(
                                                    filterRequest.value
                                                        ?.statusList,
                                                ) &&
                                                filterRequest.value?.statusList.includes(
                                                    key,
                                                )
                                            }
                                            style={{
                                                '--border-color':
                                                    colorObj?.borderColor ||
                                                    '#ccc',
                                                '--background-color':
                                                    colorObj?.backgroundColor ||
                                                    '#4CAF50',
                                            }}
                                        />
                                        <p
                                            className="m-0 font-bold flex-grow-1"
                                            style={{
                                                color: colorObj?.textColor,
                                            }}>
                                            {translateCommons(
                                                convertKeyToLabel(key),
                                            )}
                                        </p>

                                        {filterRequest.value?.statusList &&
                                        filterRequest.value?.statusList
                                            ?.length &&
                                        filterRequest.value?.statusList.includes(
                                            key,
                                        ) ? (
                                            <i className="pi pi-check-circle text-green-600"></i>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
                <div className="lg:col-9 col-12 h-screen overflow-y-auto">
                    <div className="col-12">
                        <h4 className="m-0 font-bold mb-1">
                            {translateKeys('Appointment')}
                        </h4>
                        <p className="text-700">
                            {translateCommons(
                                'The process of scheduling appointments or securing spots for various medical services',
                            )}
                        </p>
                    </div>
                    <div id={'reservation-calendar'} className="relative">
                        <FullCalendar
                            ref={calendarRef}
                            locale={
                                getLanguageCode() === 'gb'
                                    ? 'en'
                                    : getLanguageCode()
                            }
                            plugins={[
                                listPlugin,
                                timeGridPlugin,
                                interactionPlugin,
                            ]}
                            style={{minWidth: '25rem'}}
                            // Data
                            events={dataFormated}
                            // Custom & Format
                            eventClassNames={'h-max overflow-auto w-full'}
                            initialView="listWeek"
                            headerToolbar={{
                                left: 'headerCustom',
                                center: 'titleCustom timeGridWeek,listWeek todayCustom',
                                right: 'prevButton,nextButton',
                            }}
                            buttonText={{
                                week: translateCommons('Week'),
                                list: translateCommons('Day'),
                            }}
                            allDayClassNames={'text-center font-bold'}
                            allDayText={`GMT ${moment()
                                .tz(timezoneId)
                                .format('Z')}`}
                            slotLabelFormat={[
                                {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    meridiem: 'lowercase',
                                },
                            ]}
                            slotLabelClassNames={'font-semibold uppercase'}
                            customButtons={{
                                prevButton: {
                                    icon: 'fc-icon fc-icon-chevron-left',
                                    click: function (e) {
                                        calendarRef?.current?.getApi().prev()
                                        filterRequest.value = {
                                            ...filterRequest.value,
                                            dateIn: getDateOfWeek(
                                                calendarRef?.current
                                                    ?.getApi()
                                                    .getCurrentData()
                                                    ?.currentDate,
                                            ),
                                            dateTo: '',
                                            dateFrom: '',
                                        }

                                        title.value = getTitleFormated(
                                            calendarRef?.current
                                                ?.getApi()
                                                .getCurrentData()?.currentDate,
                                        )
                                    },
                                },

                                nextButton: {
                                    icon: 'fc-icon fc-icon-chevron-right',
                                    click: function (e) {
                                        calendarRef?.current?.getApi().next()
                                        filterRequest.value = {
                                            ...filterRequest.value,
                                            dateIn: getDateOfWeek(
                                                calendarRef?.current
                                                    ?.getApi()
                                                    .getCurrentData()
                                                    ?.currentDate,
                                            ),
                                            dateTo: '',
                                            dateFrom: '',
                                        }

                                        title.value = getTitleFormated(
                                            calendarRef?.current
                                                ?.getApi()
                                                .getCurrentData()?.currentDate,
                                        )
                                    },
                                },
                                titleCustom: {
                                    text: (
                                        <div className="flex align-items-center md:text-sm lg:text-md text-xs p-2 sm:w-full">
                                            <span className="flex align-items-center text-color">
                                                <h4 className="m-0 md:text-base lg:text-xl text-base">
                                                    {title.value
                                                        ? title.value
                                                        : getTitleFormated(
                                                              new Date(),
                                                          )}
                                                </h4>
                                            </span>
                                        </div>
                                    ),
                                    hint: title.value
                                        ? title.value
                                        : getTitleFormated(new Date()),
                                },
                                todayCustom: {
                                    text: renderTodayButton(),
                                    click: function (e) {
                                        calendarRef?.current?.getApi().today()
                                        filterRequest.value = {
                                            ...filterRequest.value,
                                            dateIn: getDateOfWeek(
                                                calendarRef?.current
                                                    ?.getApi()
                                                    .getCurrentData()
                                                    ?.currentDate,
                                            ),
                                        }

                                        title.value = getTitleFormated(
                                            calendarRef?.current
                                                ?.getApi()
                                                .getCurrentData()?.currentDate,
                                        )
                                    },
                                    hint: `Today`,
                                },
                                headerCustom: {
                                    text: (
                                        <div className="flex align-items-center md:text-md lg:text-lg text-sm">
                                            <span className="flex align-items-center text-color">
                                                <div className="p-2 surface-100 text-400 border-round mr-2">
                                                    <i
                                                        className="pi pi-calendar"
                                                        style={{
                                                            fontSize: '1.5rem',
                                                        }}></i>
                                                </div>
                                                <h3 className="m-0 mr-2 font-bold">
                                                    {dataFormated?.length || 0}
                                                </h3>
                                                <p className="m-0 text-500">
                                                    {translateCommons(
                                                        'total appointments',
                                                    )}
                                                </p>
                                            </span>
                                        </div>
                                    ),
                                    hint: `${translateCommons('Total')} ${
                                        dataFormated?.length || 0
                                    } ${translateCommons('appointments')}`,
                                },
                            }}
                            // Event
                            displayEventTime={true}
                            nowIndicator
                            eventOverlap={false}
                            eventMinWidth={'20rem'}
                            contentHeight={'auto'}
                            eventTimeFormat={{
                                hour: 'numeric',
                                minute: '2-digit',
                                omitZeroMinute: false,
                                meridiem: 'lowercase',
                            }}
                            eventContent={renderEventContent}
                            displayEventEnd
                            // eventClick={e => {
                            //     e.jsEvent.preventDefault()

                            //     let appointmentData = {
                            //         id: e.event.id
                            //             ? parseInt(e.event.id)
                            //             : null,
                            //         dateAndTime: e.event?.start,
                            //         ...e.event?.extendedProps,
                            //     }

                            //     dialogRef.current.showForm(appointmentData)
                            // }}
                            handleWindowResize
                            windowResize={() =>
                                calendarRef.current?.getApi()?.updateSize()
                            }
                            // On Change View
                            viewDidMount={e => {
                                currentView.value = e.view.type
                            }}
                        />
                        <ReservationDialog ref={dialogRef} />
                        {isFetching && (
                            <div className="absolute h-full w-full top-0 flex align-items-center justify-content-center surface-300 opacity-40">
                                <ProgressSpinner
                                    style={{width: '50px', height: '50px'}}
                                    strokeWidth="8"
                                    animationDuration=".5s"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <CalendarForm
                ref={calendarForm}
                calendarFormType={CalendarFormType.reservation}
            />
        </PageContentWrapper>
    )
}

export default StyledReservationDataTable
